import React, { useEffect, useState } from "react";

import { Product } from "@transfr-inc/dashboard-components/constants";
import { useFilter } from "@transfr-inc/dashboard-components/forms";

import { TraineeInsights } from "./trainee.insights";
import { TraineeSimulations } from "./trainee.simulations";

import { StatusFilter } from "@views/classrooms/details/components/classroom.modules.utils";
import { useContainer } from "@src/contexts/container-context";
import { useRole } from "@queries";
import { Role } from "@models";
import { MasteryStatus } from "@models/mastery";
import { filterUserSimCodesByCategory } from "@services/account.utils";

const getSimulationsData = (data) => {
  const sims = [];
  data.map((r) => {
    sims.push(
      ...r.simulations.map((s) => ({
        ...s,
        moduleName: r.displayName,
        moduleId: r.moduleId,
        appDescription: r.appDescription,
      }))
    );
  });
  return sims;
};

export function TraineeClassroomTsTab({
  trainee,
  classroom,
  organization,
  onRemoveTrainee,
  removeTraineeEnabled,
  isPreview,
}) {
  const [simulations, setSimulations] = useState();
  const [filteredSims, setFilteredSims] = useState();
  const [selectedStatus, setSelectedStatus] = useState();
  const [codeGeneratedMapping, setCodeGeneratedMapping] = useState([]);

  const role = isPreview ? Role.STUDENT.id : useRole();
  const isStudent = role == Role.STUDENT.id;

  const { insightsService, accountService } = useContainer();

  const simsModuleFilter = useFilter({
    data: simulations,
    searchKeys: ["moduleName"],
  });

  const statusFilter = useFilter({
    data: simulations,
    queryFn: (data, statusSelected) => {
      if (statusSelected === StatusFilter.All) {
        return data;
      } else if (statusSelected === StatusFilter.Started) {
        return data.filter(
          (d) => d.status && d?.status !== MasteryStatus.NotStarted
        );
      } else {
        return data.filter((d) => d.status === statusSelected);
      }
    },
  });

  useEffect(() => {
    const simulationsRequest = insightsService.getUserModulesWithMasteryInfo(
      organization?.code,
      trainee.userId,
      isStudent,
      classroom?.classroomId
    );

    // Sim passes are only surfaced in the student view. Retrieve any existing,
    // active per-sim codes so a reload (or a new day) doesn't hide a code that
    // is still valid in the headset.
    const existingCodesRequest =
      isStudent && classroom?.classroomId
        ? accountService
            .fetchMostRecentCodes({
              user_id: trainee.userId,
              classroom_id: classroom.classroomId,
              product: Product.TS,
            })
            .then((codes) =>
              filterUserSimCodesByCategory(codes, trainee.userId)
            )
            .catch(() => ({}))
        : Promise.resolve({});

    Promise.all([simulationsRequest, existingCodesRequest])
      .then(([response, existingCodes]) =>
        initSimulations(getSimulationsData(response), existingCodes)
      )
      .catch(setSimulations);

    return () => insightsService.cancelRequest();
  }, []);

  const initSimulations = (value, existingCodes = {}) => {
    setSimulations(value);
    statusFilter.updateData(value);
    simsModuleFilter.updateData(value);

    const initialFilter = isStudent ? StatusFilter.All : StatusFilter.Started;
    setFilteredSims(statusFilter.queryData(initialFilter));
    setSelectedStatus(initialFilter);

    const codeMapping = value.reduce((acc, sim) => {
      const simCodes = existingCodes[sim.learningExperienceId] ?? {};
      const codes = (sim.categories ?? [])
        .filter((category) => simCodes[category.categoryCode])
        .map((category) => ({
          name: category.categoryName,
          code: simCodes[category.categoryCode].code,
        }));

      acc[sim.learningExperienceId] = {
        generated: codes.length > 0,
        codes,
      };
      return acc;
    }, {});

    setCodeGeneratedMapping(codeMapping);
  };

  const onStatusSelected = (status) => {
    setSelectedStatus(status);
    const results = statusFilter.queryData(status);
    setFilteredSims(results);
  };

  const onCodeGenerated = (leId, codes) => {
    const updatedCodes = { ...codeGeneratedMapping };

    let codeList = [];
    for (const cat of codes) {
      codeList.push({ code: cat.code, name: cat.name });
    }

    updatedCodes[leId].generated = true;
    updatedCodes[leId].codes = codeList;
    setCodeGeneratedMapping(updatedCodes);
  };

  return (
    <>
      <TraineeInsights
        trainee={trainee}
        classroom={classroom}
        simulations={simulations}
        onRemoveTrainee={onRemoveTrainee}
        removeTraineeEnabled={removeTraineeEnabled}
        onStatusSelected={onStatusSelected}
        selectedStatus={selectedStatus}
        role={role}
      ></TraineeInsights>
      <TraineeSimulations
        data={filteredSims}
        trainee={trainee}
        orgCode={organization.code}
        classroom={classroom}
        codeGeneratedMapping={codeGeneratedMapping}
        onCodeGenerated={onCodeGenerated}
        role={role}
      ></TraineeSimulations>
    </>
  );
}
